import { Injectable } from "@angular/core";
import { StoreService } from "..";
import { catchError, from, map, Observable, tap } from "rxjs";
import { TherapeuticDomainApiService } from "../../services/api/therapeutic-domain-api.service";
import { TherapeuticDomain, TherapeuticDomainBase } from "../../models/therapeutic-domain.model";

@Injectable({
  providedIn: 'root'
})
export class TherapeuticDomainService {

  constructor(
    private store: StoreService,
    private therapeuticDomainApiService: TherapeuticDomainApiService
  ) { }

  get therapeuticDomains$(): Observable<TherapeuticDomain[]> {
    return this.store.state$.pipe(map(state => Object.values(state.therapeuticDomains)));
  }

  load(): Observable<TherapeuticDomain[]> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.therapeuticDomainApiService.getAll().pipe(
      tap((domains) => {
        const state = { ...this.store.state };
        domains.forEach((t) => state.therapeuticDomains[t.id] = t);
        domains.forEach((t) => state.therapeuticDomainCodes[t.code] = t.id);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading building therapeutic domains:', error);
        throw error;
      })
    );
  }

  addTherapeuticDomain(domain: TherapeuticDomainBase): Observable<TherapeuticDomain> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.therapeuticDomainApiService.add(domain)).pipe(
      tap((newDomain) => {
        this.updateState(newDomain);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error adding therapeutic domain:', error);
        throw error;
      })
    );
  }

  updateTherapeuticDomain(domain: TherapeuticDomain): Observable<TherapeuticDomain> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.therapeuticDomainApiService.update(domain)).pipe(
      tap((domain) => {
        this.updateState(domain);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error updating therapeutic domain:', error);
        throw error;
      })
    );
  }

  private updateState(domain: TherapeuticDomain) {
    const state = { ...this.store.state };
    state.therapeuticDomains[domain.id] = domain;
    state.therapeuticDomainCodes[domain.code] = domain.id;
    state.loading = false;
    this.store.setState(state);
  }

  deleteTherapeuticDomain(id: number): Observable<void> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return from(this.therapeuticDomainApiService.delete(id)).pipe(
      tap(() => {
        const state = { ...this.store.state };
        const code = state.therapeuticDomains[id]?.code;
        delete state.therapeuticDomains[id];
        delete state.therapeuticDomainCodes[code];
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error deleting therapeutic domain:', error);
        throw error;
      })
    );
  }

  clear(): void {
    const state = { ...this.store.state };
    state.therapeuticDomains = {};
    state.therapeuticDomainCodes = {};
    this.store.setState(state);
  }
}